import { useState, useEffect } from "react";
import { Loader2, Save } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { toast } from "@/components/ui/toaster";
import * as api from "@/services/api";
import type { Feature } from "@/types";

const AVAILABLE_ACTIONS = [
	"view",
	"create",
	"update",
	"delete",
	"export",
	"approve",
	"manage",
] as const;

const MODULES = [
	"core",
	"organization",
	"hris",
	"ams",
	"monitoring",
	"kpi",
	"admin",
	"subscription",
	"notifications",
	"gamification",
	"appraisal",
] as const;

const SCOPE_TYPES = [
	{ value: "collective", label: "Collective", description: "Works with org or department scope" },
	{ value: "personal", label: "Personal", description: "Can work with self scope" },
	{ value: "administrative", label: "Administrative", description: "Requires organization-level access" },
] as const;

interface EditFeatureDialogProps {
	feature: Feature;
	open: boolean;
	onOpenChange: (open: boolean) => void;
	existingModules?: string[];
}

export function EditFeatureDialog({
	feature,
	open,
	onOpenChange,
	existingModules,
}: EditFeatureDialogProps) {
	const [name, setName] = useState("");
	const [description, setDescription] = useState("");
	const [module, setModule] = useState("");
	const [selectedActions, setSelectedActions] = useState<string[]>([]);
	const [scopeType, setScopeType] = useState<string>("");
	const [isActive, setIsActive] = useState(true);

	const queryClient = useQueryClient();

	// Populate form when feature changes or dialog opens
	useEffect(() => {
		if (open && feature) {
			setName(feature.name || feature.ui?.label || feature.slug);
			setDescription("");
			setModule(feature.module || "");
			setSelectedActions([...feature.actions]);
			setScopeType(feature.scopeType || "");
			setIsActive(feature.isActive !== false);
		}
	}, [open, feature]);

	const updateMutation = useMutation({
		mutationFn: (data: Parameters<typeof api.updateFeature>[1]) =>
			api.updateFeature(feature.slug, data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["features"] });
			queryClient.invalidateQueries({ queryKey: ["dependencies"] });
			queryClient.invalidateQueries({ queryKey: ["plan-matrix"] });
			toast({
				title: "Feature updated",
				description: `Feature "${name}" has been updated successfully.`,
				variant: "success",
			});
			onOpenChange(false);
		},
		onError: (error: Error) => {
			toast({
				title: "Failed to update feature",
				description: error.message,
				variant: "destructive",
			});
		},
	});

	const toggleAction = (action: string) => {
		setSelectedActions((prev) =>
			prev.includes(action)
				? prev.filter((a) => a !== action)
				: [...prev, action],
		);
	};

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();

		if (!name || !module || selectedActions.length === 0) {
			toast({
				title: "Missing required fields",
				description: "Please fill in name, module, and select at least one action.",
				variant: "destructive",
			});
			return;
		}

		updateMutation.mutate({
			name,
			description: description || undefined,
			module,
			actions: selectedActions,
			scopeType: scopeType
				? (scopeType as "collective" | "personal" | "administrative")
				: undefined,
			isActive,
		});
	};

	const allModules = Array.from(
		new Set([...MODULES, ...(existingModules || [])]),
	).sort();

	const isValid = name && module && selectedActions.length > 0;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
				<DialogHeader>
					<DialogTitle>Edit Feature</DialogTitle>
					<DialogDescription>
						Update feature properties for{" "}
						<code className="text-xs bg-muted px-1 py-0.5 rounded">
							{feature.slug}
						</code>
					</DialogDescription>
				</DialogHeader>

				<form onSubmit={handleSubmit} className="space-y-4 mt-2">
					{/* Code (read-only) */}
					<div className="space-y-2">
						<Label>Code</Label>
						<Input
							value={feature.slug}
							disabled
							className="font-mono text-sm bg-muted"
						/>
						<p className="text-xs text-muted-foreground">
							Code cannot be changed after creation.
						</p>
					</div>

					{/* Name */}
					<div className="space-y-2">
						<Label htmlFor="edit-feature-name">
							Name <span className="text-red-500">*</span>
						</Label>
						<Input
							id="edit-feature-name"
							placeholder="e.g. Employee Leave Management"
							value={name}
							onChange={(e) => setName(e.target.value)}
						/>
					</div>

					{/* Description */}
					<div className="space-y-2">
						<Label htmlFor="edit-feature-description">Description</Label>
						<Input
							id="edit-feature-description"
							placeholder="Brief description of this feature"
							value={description}
							onChange={(e) => setDescription(e.target.value)}
						/>
					</div>

					{/* Module */}
					<div className="space-y-2">
						<Label>
							Module <span className="text-red-500">*</span>
						</Label>
						<Select value={module} onValueChange={setModule}>
							<SelectTrigger>
								<SelectValue placeholder="Select a module" />
							</SelectTrigger>
							<SelectContent>
								{allModules.map((mod) => (
									<SelectItem key={mod} value={mod}>
										{mod}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>

					{/* Actions */}
					<div className="space-y-2">
						<Label>
							Actions <span className="text-red-500">*</span>
						</Label>
						<div className="flex flex-wrap gap-3">
							{AVAILABLE_ACTIONS.map((action) => (
								<div key={action} className="flex items-center gap-2">
									<Checkbox
										id={`edit-action-${action}`}
										checked={selectedActions.includes(action)}
										onCheckedChange={() => toggleAction(action)}
									/>
									<Label
										htmlFor={`edit-action-${action}`}
										className="text-sm font-normal cursor-pointer"
									>
										{action}
									</Label>
								</div>
							))}
						</div>
						{selectedActions.length > 0 && (
							<div className="flex flex-wrap gap-1 mt-1">
								{selectedActions.map((action) => (
									<Badge key={action} variant="secondary" className="text-xs">
										{action}
									</Badge>
								))}
							</div>
						)}
					</div>

					{/* Scope Type */}
					<div className="space-y-2">
						<Label>Scope Type</Label>
						<Select value={scopeType} onValueChange={setScopeType}>
							<SelectTrigger>
								<SelectValue placeholder="Select scope type (optional)" />
							</SelectTrigger>
							<SelectContent>
								{SCOPE_TYPES.map((st) => (
									<SelectItem key={st.value} value={st.value}>
										<div>
											<span>{st.label}</span>
											<span className="text-xs text-muted-foreground ml-2">
												- {st.description}
											</span>
										</div>
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>

					{/* Active Status */}
					<div className="flex items-center justify-between rounded-lg border p-3">
						<div className="space-y-0.5">
							<Label htmlFor="edit-feature-active">Active</Label>
							<p className="text-xs text-muted-foreground">
								Inactive features won't be available in the system.
							</p>
						</div>
						<Switch
							id="edit-feature-active"
							checked={isActive}
							onCheckedChange={setIsActive}
						/>
					</div>

					<DialogFooter className="pt-4">
						<Button
							type="button"
							variant="outline"
							onClick={() => onOpenChange(false)}
							disabled={updateMutation.isPending}
						>
							Cancel
						</Button>
						<Button
							type="submit"
							disabled={!isValid || updateMutation.isPending}
						>
							{updateMutation.isPending ? (
								<>
									<Loader2 className="h-4 w-4 mr-2 animate-spin" />
									Saving...
								</>
							) : (
								<>
									<Save className="h-4 w-4 mr-2" />
									Save Changes
								</>
							)}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
